import { useState, type ReactNode } from 'react';
import { Modal } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';

export function fmtDateTime(v?: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('he-IL');
}

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast('הועתק', 'ok');
  } catch {
    toast('שגיאה בהעתקה', 'danger');
  }
}

/** Read-only value with a copy button (e.g. a secret / URL). */
export function CopyField({ value, dir = 'ltr' }: { value: string; dir?: 'ltr' | 'rtl' }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
      <code
        dir={dir}
        style={{
          flex: 1,
          padding: '8px 10px',
          background: 'var(--surface-2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-2)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--t-sm)',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </code>
      <button type="button" className="btn" onClick={() => void copyText(value)} aria-label="העתק">
        <Icon name="copy" size={14} />
      </button>
    </div>
  );
}

/** One-time secret reveal modal — value shown once, with a copy button + warning. */
export function SecretModal({
  title,
  label,
  secret,
  onClose,
}: {
  title: string;
  label: string;
  secret: string;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={title}
      footer={<button type="button" className="btn primary" onClick={onClose}>סגור</button>}
    >
      <div className="stack">
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: 'var(--pad-card)',
            background: 'var(--surface-2)',
            border: '1px solid var(--warn, var(--line))',
            borderRadius: 'var(--r-2)',
          }}
        >
          <Icon name="warn" size={16} color="var(--warn)" />
          <span style={{ fontSize: 'var(--t-sm)' }}>
            שמור את הערך עכשיו — הוא לא יוצג שוב לאחר סגירת החלון.
          </span>
        </div>
        <div className="stack" style={{ gap: 4 }}>
          <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{label}</span>
          <CopyField value={secret} />
        </div>
      </div>
    </Modal>
  );
}

/** Collapsible pretty-printed JSON payload. */
export function JsonDetails({ label, value }: { label?: string; value: unknown }) {
  const text = (() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  })();
  return (
    <details style={{ fontSize: 'var(--t-xs)' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>{label ?? 'תוכן'}</summary>
      <pre
        dir="ltr"
        style={{
          margin: '6px 0 0',
          padding: 10,
          background: 'var(--surface-2)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-2)',
          overflowX: 'auto',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--t-xs)',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {text}
      </pre>
    </details>
  );
}

/** Small labelled section wrapper used inside the automation builder. */
export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <h4 style={{ margin: 0, fontSize: 'var(--t-md)', fontWeight: 600 }}>{title}</h4>
      {children}
    </div>
  );
}

/** Copy button that flips label after use — handy in usage-example blocks. */
export function useCopyState() {
  const [copied, setCopied] = useState(false);
  return {
    copied,
    copy: async (text: string) => {
      await copyText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
  };
}
